import { useQuery } from '@tanstack/react-query';
import { Input, Select, Switch } from '@/components/ui';
import { getGoogleAccounts, patchClientConnections } from '@/data/client';
import type { ClientFormProps } from './types';
import { CONNECTION_TABS, connectionsFromClient, pickConnections } from './data';
import { useDraft } from './useDraft';
import { SaveFooter } from './SaveFooter';

const KEYS = CONNECTION_TABS.traffic;

/* trafficIQ and reviewsIQ inputs. */
export function TrafficForm({ client, onSaved, onDirtyChange }: ClientFormProps) {
  const f = useDraft({
    formId: 'traffic',
    initial: connectionsFromClient(client),
    keys: KEYS,
    persist: (d) => patchClientConnections(client.orgId, pickConnections(d, KEYS)),
    onSaved, onDirtyChange,
  });
  const { draft: conn, set: setC } = f;
  /* Google caps one identity at 100 Analytics accounts and the first service
     account is full, so each client records which one reads its traffic. The
     email shown is the one to add as Viewer on the GA4 property and as a user
     on the Search Console site. */
  const { data: ga } = useQuery({ queryKey: ['admin', 'google-accounts'], queryFn: getGoogleAccounts, staleTime: 5 * 60_000 });
  const accounts = ga?.accounts ?? [];
  const chosen = accounts.find((a) => a.slot === (conn.googleSa === '2' ? '2' : '1'));

  return (
    <div className="admin-form">
      <div className="admin-form__body">
        <section className="admin-sect">
          <h4 className="admin-sect__title">Traffic</h4>
          <div className="admin-prof">
            <div className="admin-prof__row">
              <Input
                label="GA4 property ID"
                placeholder="e.g. 412889006"
                value={conn.ga4PropertyId}
                onChange={(e) => setC('ga4PropertyId', e.target.value)}
              />
              <Input
                label="Search Console site URL"
                placeholder="https://example.com/  or  sc-domain:example.com"
                value={conn.gscSiteUrl}
                onChange={(e) => setC('gscSiteUrl', e.target.value)}
              />
            </div>
            <div className="admin-prof__row">
              <Select
                label="Google service account"
                value={conn.googleSa === '2' ? '2' : '1'}
                onChange={(e) => setC('googleSa', e.target.value)}
                options={[
                  { value: '1', label: `Account 1${accounts.find((a) => a.slot === '1') ? ` — ${accounts.find((a) => a.slot === '1')!.email}` : ''}` },
                  { value: '2', label: `Account 2${accounts.find((a) => a.slot === '2') ? ` — ${accounts.find((a) => a.slot === '2')!.email}` : ' (secret not set)'}` },
                ]}
                hint={chosen
                  ? `Add ${chosen.email} as Viewer on the GA4 property and as a user on the Search Console site. Account 1 is at Google's 100-account cap — use Account 2 for new clients.`
                  : 'Account 1 is at Google\'s 100-account cap — use Account 2 for new clients.'}
              />
            </div>
            <Switch
              label="trafficIQ period comparison"
              checked={conn.trafficCompareEnabled}
              onChange={(e) => setC('trafficCompareEnabled', e.target.checked)}
            />
            {!conn.trafficCompareEnabled && (
              <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>
                Off: this client&rsquo;s users see trafficIQ without the &ldquo;Compare:&rdquo; filter or period-over-period deltas. Triton admins always see it. Turn on once the account has enough history for the comparison to mean something.
              </p>
            )}
          </div>
        </section>

        <section className="admin-sect">
          <h4 className="admin-sect__title">Reviews</h4>
          <div className="admin-prof">
            <div className="admin-prof__row">
              <Input
                label="Triton Reviews client ID"
                placeholder="e.g. 167232"
                value={conn.gatherupClientId}
                onChange={(e) => setC('gatherupClientId', e.target.value)}
              />
              <Input
                label="Flag reviews as stale after"
                hint="Days without a new review before the client is flagged."
                type="number"
                min={1}
                className="admin-narrowfield"
                value={conn.reviewsStaleDays}
                onChange={(e) => setC('reviewsStaleDays', e.target.value)}
              />
            </div>
          </div>
        </section>
      </div>
      <SaveFooter dirty={f.dirty} saving={f.saving} err={f.err} onSave={f.save} label="Save traffic & reviews" />
    </div>
  );
}
